/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx, css } from "@emotion/core";
import type { ReactNode } from "react";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useIntl } from "react-intl";
import type { OnChangeHandlerFunc, SuggestionDataItem } from "react-mentions";
import { Mention, MentionsInput } from "react-mentions";
import type { CommentMention } from "@certa/types";
import { MentionListItem } from "./MentionListItem";
import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import { useUsersWhoCanBeMentioned } from "@certa/queries/hooks/userMentions.hooks";
import { useDebouncedValue } from "@certa/common/hooks/useDebounce";
import { useEnableSearchAPIOnMentionableThreadUsers } from "@certa/common/toggles/useEnableSearchAPIOnMentionableThreadUsers";
import {
  limitMentionsCount,
  filterMentionsByText,
  filterGroupsByText
} from "@certa/common/utils/mention";
import { CatalystColors } from "@certa/catalyst/constants/styles";

const MENTIONS_MARKUP = "~[__display__](__id__)";
// Below markup is used to show invalid mentions in red color
const INVALID_MENTIONS_MARKUP = "-[__display__](__id__)";

const fontStylings = `
  font-family: "Inter", sans-serif, "system-ui" !important;

  /* Variant: Body */
  font-weight: 400 !important;
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
  letter-spacing: 0.009375rem !important;
`;

const commonLayoutStylings = `
  max-height: 30vh;
  min-height: 40px;
  padding: 1rem 1rem 3.5rem 1rem;
  border: 2px solid transparent !important;
`;

const INPUT_STYLES = (
  isMobileResolution: boolean,
  customHeight?: number
) => css`
  background-color: ${CatalystColors.NEUTRAL_100};
  border: 1px solid ${CatalystColors.BRAND_200};
  box-shadow: 0px 2px 8px 0px rgba(0, 22, 78, 0.1);
  border-radius: 0.75rem;

  .comments-textarea-new__control {
    height: ${isMobileResolution && "2.625rem !important"};
    ${customHeight && `height: ${customHeight}px;`};
  }

  /* Suggestions input + highlighter stylings */
  .comments-textarea-new__input {
    margin: 0px;
    border-radius: 0.75rem !important;
    border: none !important;
    transition: border 0s !important;
    color: var(--neutral-100) !important;
    /* width: 100% !important; */
    overflow-y: auto !important;
    ${commonLayoutStylings}
    ${fontStylings}
    &::placeholder {
      color: var(--neutral-70) !important;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;

      /* Variant: Body */
      font-weight: 400 !important;
      font-size: 0.875rem !important;
      line-height: 1.25rem !important;
      letter-spacing: 0.009375rem !important;
    }
    &:placeholder-shown {
      text-overflow: ellipsis;
    }
    &:focus {
      border: 2px solid var(--colors-brand-400) !important;
      outline: 0px !important;
    }
  }
  .comments-textarea-new__highlighter {
    border-radius: var(--s1) !important;
    ${commonLayoutStylings}
    ${fontStylings}
  }

  /* Suggestions list stylings */
  .comments-textarea-new__suggestions__list {
    background-color: var(--neutral-0);
    box-shadow: 0px 2px 16px rgba(0, 22, 78, 0.1);
    border-radius: var(--s2);
    max-height: 323px;
    width: ${isMobileResolution ? "18rem" : "26rem"};
    overflow-y: scroll;
    padding: var(--s4) !important;
  }
  .comments-textarea-new__suggestions__item--focused {
    background-color: var(--brand-35);
    border-radius: var(--s2);
    span {
      font-weight: var(--bold);
    }
  }
`;

export type MenionSupportedInputProps = {
  onChange?: OnChangeHandlerFunc;
  disabled: boolean;
  /** Contains only groups or users and groups depending on feature toggle */
  mentions: CommentMention[];
  message: string;
  onSubmit?: Function;
  placeholder?: string;
  customHeight?: number;
  autoFocus?: boolean;
  /** For dynamic user search via new API */
  threadId?: number;
};

/**
 * CommentMentionsInput houses the MentionsInput component with props set
 * according to the needs to comment feature, plugged in with available
 * mentions and ability to post comment on Enter (or not).
 */
export const MentionSupportedInput = memo(
  forwardRef<HTMLTextAreaElement, MenionSupportedInputProps>((props, ref) => {
    const intl = useIntl();
    const {
      onChange,
      onSubmit,
      disabled: isDisabled,
      mentions,
      message,
      threadId,
      placeholder = intl.formatMessage({
        id: "comments.commentsMessageInputPlaceholder",
        defaultMessage: "Type your comment here... Use @ to mention people."
      })
    } = props;

    const { isMobileResolution } = useScreenResolution();

    // Feature toggle to control new API usage
    const isSearchAPIEnabled = useEnableSearchAPIOnMentionableThreadUsers();
    const callbackRef = useRef<((data: SuggestionDataItem[]) => void) | null>(
      null
    );

    // State for current search term to enable debouncing
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebouncedValue(searchTerm, 300); // 300ms debounce

    // Use React Query hook for caching and better state management
    // Only enabled when feature toggle is ON
    const { data: users = [], error: userSearchError } =
      useUsersWhoCanBeMentioned(
        {
          threadId: threadId || 0,
          searchTerm: debouncedSearchTerm
        },
        {
          enabled:
            isSearchAPIEnabled && !!threadId && debouncedSearchTerm.length >= 3,
          onError: () => {
            console.error(
              "Failed to fetch users for mentions:",
              userSearchError
            );
          }
        }
      );

    useEffect(() => {
      if (users && callbackRef.current) {
        const filteredGroups = filterGroupsByText(
          mentions,
          debouncedSearchTerm
        );
        const allMentions = [...filteredGroups, ...users];
        const limitedMentions = limitMentionsCount(allMentions);
        callbackRef.current(limitedMentions);
      }
    }, [users, debouncedSearchTerm, mentions]);

    /**
     * Handles mention search with debouncing and React Query caching
     * Respects feature toggle to decide between new API and old behavior
     */
    const handleMentionSearch = useCallback(
      (searchText: string, callback: (data: SuggestionDataItem[]) => void) => {
        const filterMentionsFromProp = () => {
          const filteredMentions = filterMentionsByText(mentions, searchText);
          const limitedMentions = limitMentionsCount(filteredMentions);
          return limitedMentions;
        };

        // If toggle is OFF, use original behavior (local filtering only)
        if (!isSearchAPIEnabled) {
          callback(filterMentionsFromProp());
          return;
        }

        // NEW API BEHAVIOR (when toggle is ON):
        // Update search term for debouncing (triggers API call if needed)
        setSearchTerm(searchText);

        callbackRef.current = callback;

        if (!threadId || searchText.length < 3) {
          // For < 3 characters or no threadId, filter locally
          callback(filterMentionsFromProp());
          return;
        }

        // keep showing previous results
        const filteredGroups = filterGroupsByText(mentions, searchText);
        const allMentions = [...filteredGroups, ...users];
        const limitedMentions = limitMentionsCount(allMentions);
        callback(limitedMentions);
      },
      [isSearchAPIEnabled, threadId, mentions, users]
    );

    /**
     * handleOnKeypress is responsible for posting message when user presses
     * the Shift key + enter key. To add a new line, user can use Enter, as usual.
     */
    const handleOnKeypress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!e.shiftKey && e.key === "Enter") {
        onSubmit?.();
      }
    };

    const renderSuggestionList = (
      entry: SuggestionDataItem,
      search: any,
      highlightedDisplay: ReactNode,
      index: number
    ) => (
      <MentionListItem
        label={entry.display}
        isUserGroup={String(entry.id).includes("g")}
      />
    );

    return (
      <MentionsInput
        inputRef={ref}
        value={message}
        onChange={onChange}
        allowSpaceInQuery
        allowSuggestionsAboveCursor
        placeholder={placeholder}
        disabled={isDisabled}
        onKeyPress={handleOnKeypress}
        autoFocus={props.autoFocus}
        aria-label={"mention-input"}
        className="comments-textarea-new"
        css={INPUT_STYLES(isMobileResolution, props?.customHeight)}
      >
        <Mention
          appendSpaceOnAdd={true}
          trigger="@"
          displayTransform={(id: string, display: string) => `@${display}`}
          data={handleMentionSearch}
          markup={MENTIONS_MARKUP}
          renderSuggestion={renderSuggestionList}
          css={css`
            border-radius: var(--s1);
            background-color: var(--colors-blueberry-200);
          `}
        />
        {/* 
          Below mention is used to show invalid mentions in red color 
          We know that this is not the right way to do it.
          But With given react-mentions library, we are not able to find any other way to do it.
        */}
        <Mention
          appendSpaceOnAdd={true}
          trigger="@"
          displayTransform={(id: string, display: string) => {
            return `@${display}`;
          }}
          data={[]}
          markup={INVALID_MENTIONS_MARKUP}
          renderSuggestion={renderSuggestionList}
          css={css`
            border-radius: var(--s1);
            background-color: var(--colors-red-200);
            border: 1px dotted var(--colors-red-600);
          `}
        />
      </MentionsInput>
    );
  })
);
